<template>
  <div class="column content-box">
    <woot-loading-state
      v-if="isLoading"
      :message="$t('OPPORTUNITY_PIPELINES.LIST.LOADING')"
    />

    <div v-else-if="!pipelines.length" class="empty-wrap">
      <empty-state
        :image-src="emptyListImage"
        :title="$t('OPPORTUNITY_PIPELINES.LIST.EMPTY_TITLE')"
        :message="$t('OPPORTUNITY_PIPELINES.LIST.EMPTY_MESSAGE')"
      />
    </div>

    <div v-else class="row">
      <div class="small-8 columns with-right-space">
        <div
          v-for="pipeline in pipelines"
          :key="pipeline.id"
          class="pipeline-card"
        >
          <div>
            <h4 class="pipeline-title">{{ pipeline.name }}</h4>
            <p class="pipeline-description">{{ pipeline.description || '-' }}</p>
            <p class="pipeline-metrics">
              {{ $t('OPPORTUNITY_PIPELINES.LIST.CARD.USERS') }}: {{ pipeline.users_count }} |
              {{ $t('OPPORTUNITY_PIPELINES.LIST.CARD.INBOXES') }}: {{ pipeline.inboxes_count }} |
              {{ $t('OPPORTUNITY_PIPELINES.LIST.CARD.STAGES') }}: {{ pipeline.stages_count }}
            </p>
          </div>

          <div class="pipeline-actions">
            <woot-switch
              :value="pipeline.status === 'active'"
              @input="toggleStatus(pipeline)"
            />
            <router-link
              v-tooltip.top="negotiationFormTooltip(pipeline)"
              class="negotiation-form-link"
              :class="negotiationFormClass(pipeline)"
              :aria-label="negotiationFormTooltip(pipeline)"
              :to="{
                name: 'settings_opportunity_pipelines_negotiation_form',
                params: { pipelineId: pipeline.id },
              }"
            >
              <fluent-icon icon="document" size="28" />
            </router-link>
            <router-link
              class="button small"
              :to="{
                name: 'settings_opportunity_pipelines_edit',
                params: { pipelineId: pipeline.id },
              }"
            >
              {{ $t('OPPORTUNITY_PIPELINES.LIST.CARD.CONFIGURE') }}
            </router-link>
          </div>
        </div>
      </div>

      <div class="small-4 columns">
        <span v-dompurify-html="$t('OPPORTUNITY_PIPELINES.SIDEBAR_TXT')" />
      </div>
    </div>

    <woot-delete-modal
      v-if="showDeactivateModal"
      :show.sync="showDeactivateModal"
      :on-close="cancelDeactivate"
      :on-confirm="confirmDeactivate"
      :title="$t('OPPORTUNITY_PIPELINES.LIST.DEACTIVATE_CONFIRM.TITLE')"
      :message="$t('OPPORTUNITY_PIPELINES.LIST.DEACTIVATE_CONFIRM.MESSAGE')"
      :confirm-text="$t('OPPORTUNITY_PIPELINES.LIST.DEACTIVATE_CONFIRM.YES')"
      :reject-text="$t('OPPORTUNITY_PIPELINES.ACTIONS.CANCEL')"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import accountMixin from '../../../../mixins/account';
import EmptyState from '../../../../components/widgets/EmptyState';
import emptyListImage from '../../../../assets/images/opportunity-pipelines/empty-list.svg';

export default {
  components: {
    EmptyState,
  },
  mixins: [alertMixin, accountMixin],
  data() {
    return {
      emptyListImage,
      showDeactivateModal: false,
      pendingPipeline: null,
    };
  },
  computed: {
    ...mapGetters({
      pipelines: 'opportunityPipelines/getPipelines',
      uiFlags: 'opportunityPipelines/getUIFlags',
    }),
    isLoading() {
      return this.uiFlags.isFetching;
    },
  },
  methods: {
    negotiationFormTooltip(pipeline) {
      const negotiationForm = pipeline.negotiation_form;
      if (!negotiationForm) {
        return this.$t(
          'OPPORTUNITY_PIPELINES.LIST.CARD.NEGOTIATION_FORM_EMPTY'
        );
      }
      if (negotiationForm.status !== 'active') {
        return this.$t(
          'OPPORTUNITY_PIPELINES.LIST.CARD.NEGOTIATION_FORM_INACTIVE'
        );
      }
      return this.$t(
        'OPPORTUNITY_PIPELINES.LIST.CARD.NEGOTIATION_FORM_ACTIVE',
        { count: negotiationForm.questions_count }
      );
    },
    negotiationFormClass(pipeline) {
      const negotiationForm = pipeline.negotiation_form;
      return {
        'negotiation-form-link--active':
          !!negotiationForm && negotiationForm.status === 'active',
        'negotiation-form-link--empty': !negotiationForm,
      };
    },
    async fetchPipelines() {
      try {
        await this.$store.dispatch('opportunityPipelines/get');
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.FETCH_PIPELINES'));
      }
    },
    toggleStatus(pipeline) {
      const isDeactivating = pipeline.status === 'active';

      if (isDeactivating && Number(pipeline.opportunities_count) > 0) {
        this.pendingPipeline = pipeline;
        this.showDeactivateModal = true;
        return;
      }

      this.performToggleStatus(pipeline);
    },
    cancelDeactivate() {
      this.showDeactivateModal = false;
      this.pendingPipeline = null;
    },
    async confirmDeactivate() {
      const pipeline = this.pendingPipeline;
      this.showDeactivateModal = false;
      this.pendingPipeline = null;

      if (pipeline) {
        await this.performToggleStatus(pipeline);
      }
    },
    async performToggleStatus(pipeline) {
      try {
        await this.$store.dispatch('opportunityPipelines/toggleStatus', pipeline.id);
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.TOGGLE_PIPELINE'));
      }
    },
  },
  mounted() {
    this.fetchPipelines();
  },
  activated() {
    this.fetchPipelines();
  },
};
</script>

<style lang="scss" scoped>
.empty-wrap {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 50vh;
}

.empty-wrap::v-deep {
  .empty-state {
    max-width: 48rem;
    text-align: center;
  }

  .empty-state__title {
    font-size: var(--font-size-medium);
    font-weight: var(--font-weight-semibold);
  }

  .empty-state__message {
    color: var(--s-700);
    line-height: 1.6;
  }
}

.pipeline-card {
  align-items: center;
  background: var(--white);
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-medium);
  box-shadow: 0 0.6rem 1.6rem rgba(16, 24, 40, 0.04);
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--space-small);
  padding: var(--space-normal);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.pipeline-card:hover {
  box-shadow: 0 1rem 2.2rem rgba(16, 24, 40, 0.08);
  transform: translateY(-1px);
}

.pipeline-title {
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-semibold);
  line-height: 1.4;
  margin-bottom: var(--space-micro);
}

.pipeline-description {
  color: var(--s-700);
  line-height: 1.6;
  margin-bottom: var(--space-micro);
}

.pipeline-metrics {
  color: var(--s-600);
  font-size: var(--font-size-mini);
  margin: 0;
}

.pipeline-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-small);
  justify-content: flex-end;
}

.negotiation-form-link {
  align-items: center;
  border-radius: var(--border-radius-normal);
  color: var(--s-600);
  display: inline-flex;
  justify-content: center;
  padding: var(--space-micro);
  position: relative;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: var(--s-50);
    color: var(--s-800);
  }
}

.negotiation-form-link--empty {
  color: var(--s-400);
}

.negotiation-form-link--active {
  color: var(--w-600);

  &:hover {
    background: var(--w-25);
    color: var(--w-700);
  }
}

@media screen and (max-width: 768px) {
  .pipeline-card {
    align-items: flex-start;
    flex-direction: column;
    gap: var(--space-small);
  }

  .pipeline-actions {
    justify-content: flex-start;
    width: 100%;
  }
}
</style>
